import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { Button, Card, CardContent, Typography, Chip, Box } from '@mui/material';
import { Visibility } from '@mui/icons-material';
import { useAuth } from '../../AuthContext';
import ClassDetails from '../Classes/ClassDetails'; // Import ClassDetails component
import configData from '../../configure';

const ClassesTab = ({ selectedUser }) => {
  const [classesData, setClassesData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isViewScreen, setIsViewScreen] = useState(false); // State to toggle view
  const [viewingClass, setViewingClass] = useState(null); // State for selected class
  const { axios, token } = useAuth();
  const [courseDetailsCache, setCourseDetailsCache] = useState({}); // Cache for course details

  const userId = selectedUser.rawData?.user?.userId;
  console.log("facultyid" , userId)

  useEffect(() => {
    const fetchClassesData = async () => {
      if (!userId) {
        toast.error('No user ID provided');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        // Fetch course plans
        const coursePlanResponse = await axios.get(
    `${configData.GET_COURSE_PLAN}/filter?filterType=faculty&id=${userId}`,
    {
            headers: {
              'Accept': 'application/json',
              'Authorization': token,
            }
          }
        );

        const coursePlans = coursePlanResponse.data;
        const classIds = [...new Set(coursePlans.map(plan => plan.classId))];
        const courseIds = [...new Set(coursePlans.map(plan => plan.courseId))];

        // Fetch course details for caching
        const courseDetailsPromises = courseIds.map(async (courseId) => {
          if (!courseDetailsCache[courseId]) {
            try {
              const courseResponse = await axios.get(
                `${configData.COURSE_BY_ID}/${courseId}`,
                {
                  headers: {
                    'Accept': 'application/json',
                    'Authorization': token,
                  }
                }
              );
              return { [courseId]: courseResponse.data };
            } catch (error) {
              console.error(`Failed to fetch course ${courseId}:`, error);
              return { [courseId]: null };
            }
          }
          return { [courseId]: courseDetailsCache[courseId] };
        });

        const courseDetailsArray = await Promise.all(courseDetailsPromises);
        const newCourseDetailsCache = courseDetailsArray.reduce((acc, course) => ({ ...acc, ...course }), courseDetailsCache);
        setCourseDetailsCache(newCourseDetailsCache);

        // Fetch class details for each class ID
        const classDetailsPromises = classIds.map(async (classId) => {
          const classResponse = await axios.get(
            `${configData.CLASS_BY_ID}/${classId}`,
            {
              headers: {
                'Accept': 'application/json',
                'Authorization': token,
              }
            }
          );
        
          const classData = classResponse.data;
          const coursePlansForClass = coursePlans.filter(plan => plan.classId === classId);
          
          // Get assigned subjects from subtopics
          const assignedSubjects = [...new Set(coursePlansForClass.map(plan => 
            plan.subtopicsPlanList[0]?.subtopicName.split(' in ')[0] || 'Unknown'
          ))];

          // Get assigned courses for this class
          const assignedCourseIds = [...new Set(coursePlansForClass.map(plan => plan.courseId))];
          const assignedCourses = assignedCourseIds.map(courseId => {
            const courseDetails = newCourseDetailsCache[courseId];
            return {
              courseId,
              courseName: courseDetails?.courseDetails?.courseName || 'Unknown Course',
              courseCode: courseDetails?.courseCode || 'N/A'
            };
          });

          // Calculate progress based on subtopics status
          const totalSubtopics = coursePlansForClass.reduce((sum, plan) => 
            sum + plan.subtopicsPlanList.length, 0);
          const completedSubtopics = coursePlansForClass.reduce((sum, plan) => 
            sum + plan.subtopicsPlanList.filter(subtopic => subtopic.status === 'Completed').length, 0);
          const progress = totalSubtopics > 0 ? Math.round((completedSubtopics / totalSubtopics) * 100) : 0;

          // Generate subject colors
          const subjectColors = assignedSubjects.reduce((acc, subject, index) => {
            const colors = ['#2196F3', '#FF9800', '#4CAF50', '#F44336'];
            acc[subject] = colors[index % colors.length];
            return acc;
          }, {});

          // Generate course colors
          const courseColors = assignedCourses.reduce((acc, course, index) => {
            const colors = ['#9C27B0', '#3F51B5', '#00BCD4', '#795548'];
            acc[course.courseId] = colors[index % colors.length];
            return acc;
          }, {});

          return {
            id: classId,
            className: classData.className,
            sectionName: classData.sectionName, // Match ClassDetails prop expectation
            students: 30,
            subjects: assignedSubjects.length,
            assignedSubjects: assignedSubjects || [],
            assignedCourses: assignedCourses || [], // Add assigned courses with default
            subjectColors,
            courseColors, // Add course colors
            highlighted: classId === 'f84cff1b-9bc7-4bdf-9f1b-bde96da281ee'
          };
        });

        const classes = await Promise.all(classDetailsPromises);
        setClassesData(classes);
        setLoading(false);
      } catch (err) {
        toast.error('Failed to fetch class data');
        setLoading(false);
        console.error(err);
      }
    };

    fetchClassesData();
  }, [userId, axios, token]);

  const handleViewClass = (classData) => {
    setViewingClass({
      ...classData,
      classId: classData.id, // Ensure classId is passed to ClassDetails
      sectionName: classData.sectionName,
      courseNames: (classData.assignedCourses || []).map(course => course.courseName).join(', ') // Set courseNames from assigned courses with null check
    });
    setIsViewScreen(true);
  };

  const handleBackToList = () => {
    setIsViewScreen(false);
    setViewingClass(null);
  };

  if (loading) {
    return <Typography>Loading classes...</Typography>;
  }



  if (isViewScreen && viewingClass) {
    return (
      <ClassDetails 
        viewingClass={viewingClass}
        onBack={handleBackToList}
        orgId={userId} // Using userId as orgId for consistency
        token={token}
        axios={axios}
        courseDetailsCache={courseDetailsCache}
        setCourseDetailsCache={setCourseDetailsCache}
        setClasses={setClassesData}
        classes={classesData}
      />
    );
  }

  return (
    <div style={{ padding: '20px' }}>
      <div style={{ marginBottom: '20px' }}>
        <Typography variant="h5" fontWeight="bold" style={{ marginBottom: '8px' }}>
          My Classes
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Classes and sections you are assigned to teach
        </Typography>
      </div>

      <div 
        style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', 
          gap: '20px' 
        }}
      >
        {classesData.map((classItem) => (
          <Card 
            key={classItem.id} 
            style={{ 
              borderRadius: '12px',
              border: classItem.highlighted ? '2px solid #2196F3' : '1px solid #e0e0e0',
              boxShadow: classItem.highlighted 
                ? '0 4px 12px rgba(33, 150, 243, 0.15)' 
                : '0 2px 8px rgba(0,0,0,0.1)'
            }}
          >
            <CardContent style={{ padding: '20px' }}>
              <div style={{ marginBottom: '20px' }}>
                <Typography variant="h6" fontWeight="bold" style={{ marginBottom: '4px' }}>
                  {classItem.className}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {classItem.sectionName}
                </Typography>
              </div>

              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                marginBottom: '20px',
                textAlign: 'center'
              }}>
              </div>

              {/* Assigned Courses Section */}
              <div style={{ marginBottom: '16px' }}>
                <Typography variant="body2" color="text.secondary" style={{ marginBottom: '8px' }}>
                  Assigned Courses
                </Typography>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                  {(classItem.assignedCourses || []).map((course) => (
                    <Chip
                      key={course.courseId}
                      label={`${course.courseCode} - ${course.courseName}`}
                      size="small"
                      style={{
                        backgroundColor: classItem.courseColors?.[course.courseId] || '#e0e0e0',
                        color: 'white',
                        fontSize: '11px',
                        height: '24px'
                      }}
                    />
                  ))}
                </div>
              </div>

              {/* Assigned Subjects Section */}
              {/* <div style={{ marginBottom: '20px' }}>
                <Typography variant="body2" color="text.secondary" style={{ marginBottom: '8px' }}>
                  Assigned Subjects
                </Typography>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                  {(classItem.assignedSubjects || []).map((subject) => (
                    <Chip
                      key={subject}
                      label={subject}
                      size="small"
                      style={{
                        backgroundColor: classItem.subjectColors?.[subject] || '#e0e0e0',
                        color: 'white',
                        fontSize: '11px',
                        height: '24px'
                      }}
                    />
                  ))}
                </div>
              </div> */}

              {/* <Button
                variant="contained"
                fullWidth
                startIcon={<Visibility />}
                onClick={() => handleViewClass(classItem)}
                style={{
                  backgroundColor: '#2196F3',
                  color: 'white',
                  textTransform: 'none',
                  borderRadius: '8px',
                  padding: '10px',
                  fontWeight: '500'
                }}
              >
                View Class
              </Button> */}
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default ClassesTab;